import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { FileUploadZone } from '../pilgrimGuide/upload/FileUploadZone';
import { ValidationErrors } from '../pilgrimGuide/upload/ValidationErrors';
import { WarningMessage } from '../pilgrimGuide/upload/WarningMessage';
import { Download, Info, Users } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { useAccommodationStore } from '../../stores/accommodationStore';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
  hotelName: string;
};

export const RemovePilgrimsModal = ({ isOpen, onClose, tableId, hotelName }: Props) => {
  const { translations } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { removeAllAssignments } = useAccommodationStore();

  const handleDownloadTemplate = () => {
    // Create a simple template with passport column
    const template = [
      { passport: '123456789' },
      { passport: '987654321' },
      { passport: '456789123' }
    ];

    // Create worksheet with the sample data
    const ws = XLSX.utils.json_to_sheet(template);
    
    // Add column headers
    XLSX.utils.sheet_add_aoa(ws, [['passport']], { origin: 'A1' });
    
    // Set column width
    ws['!cols'] = [{ wch: 20 }]; // passport column width
    
    // Create workbook and add sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    
    // Generate filename
    const filename = `remove-pilgrims-template.xlsx`;
    
    // Write file and download
    XLSX.writeFile(wb, filename);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setIsLoading(true);
    try {
      // Call the removeAllAssignments function with the Excel file
      await removeAllAssignments(tableId, [], [], file);
      
      toast.success('Successfully removed pilgrims based on the uploaded file');
      onClose();
    } catch (error: any) {
      console.error('Error processing file:', error);
      toast.error(error.message || 'Failed to process the file');
      setValidationErrors([error.message || 'Failed to process the file']);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.accommodationTable?.removePilgrims || 'Remove Specific Pilgrims'}
    >
      <div className="space-y-6">
        {/* Information section */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 p-4 rounded-lg flex items-start gap-3"
        >
          <Info className="text-blue-600 shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Remove Specific Pilgrims</h4>
            <p className="text-sm text-blue-700">
              Upload an Excel file with passport numbers of pilgrims you want to remove from <span className="font-semibold">{hotelName}</span>. 
              The system will find and remove all matching pilgrims from the accommodation.
            </p>
          </div>
        </motion.div>

        {/* Statistics about current accommodations */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Users size={18} className="text-primary-500" />
            <h3 className="font-medium">Hotel: {hotelName}</h3>
          </div>
          <p className="text-sm text-gray-600">
            Upload a list of passport numbers for the specific pilgrims you want to remove from this hotel accommodation.
          </p>
        </div>

        {/* Template Download Button */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.accommodationTable?.downloadTemplate || 'Download Excel Template'}</span>
        </button>

        {/* File Upload */}
        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
          }}
        />

        <ValidationErrors errors={validationErrors} />
        
        {/* Instructions */}
        <div className="bg-gray-50 p-4 rounded-lg text-gray-700 text-sm">
          <h4 className="font-medium mb-2">Instructions:</h4>
          <ul className="list-disc ml-5 space-y-1">
            <li>The Excel file must contain a column named "passport"</li>
            <li>Enter one passport number per row</li>
            <li>Pilgrims not found in the accommodation will be ignored</li>
            <li>This action will update PilgrimSelector accommodation status</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.common?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={isLoading || !file}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white
              ${!file || isLoading
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-red-500 hover:bg-red-600'}`}
          >
            {isLoading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                <span>{translations?.accommodationTable?.processing || 'Processing...'}</span>
              </>
            ) : (
              <span>{translations?.accommodationTable?.removePilgrims || 'Remove Pilgrims'}</span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};